import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BottomNav from "./UI/bottomNav";
import { googleSignOut } from "../services/googleSignUp";
import { Capacitor } from "@capacitor/core";
import {
  deleteUser,
  reauthenticateWithCredential,
  GoogleAuthProvider,
  OAuthProvider,
} from "firebase/auth";
import { auth } from "../config/signup.config";
import { ref, remove, get, getDatabase } from "firebase/database";
import { GoogleAuth } from "@codetrix-studio/capacitor-google-auth";
import { SignInWithApple } from "@capacitor-community/apple-sign-in";

function Layout({ children, isAdmin = true, isWaiter = false }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  const isIOS = Capacitor.getPlatform() === "ios";

  const handleLogout = async () => {
    setMenuOpen(false);
    await googleSignOut();
    localStorage.clear();
    navigate("/");
  };

  // Re-authenticate using whichever provider the user signed in with,
  // then delete. Firebase requires fresh credentials for account deletion.
  const reauthenticate = async () => {
    const user = auth.currentUser;
    if (!user) return;
    const providerId = user.providerData?.[0]?.providerId;

    if (providerId === "apple.com") {
      const rawNonce = Array.from(
        crypto.getRandomValues(new Uint8Array(32)),
        (b) => b.toString(16).padStart(2, "0")
      ).join("");
      const hashBuf = await crypto.subtle.digest(
        "SHA-256",
        new TextEncoder().encode(rawNonce)
      );
      const hashedNonce = Array.from(new Uint8Array(hashBuf))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      const result = await SignInWithApple.authorize({
        clientId: "com.pazhanam.waitlessq",
        redirectURI: "https://waiting-list-easy.firebaseapp.com/__/auth/handler",
        scopes: "email name",
        nonce: hashedNonce,
      });
      const credential = new OAuthProvider("apple.com").credential({
        idToken: result.response.identityToken,
        rawNonce,
      });
      await reauthenticateWithCredential(user, credential);
    } else {
      // Google
      const IOS_CLIENT_ID =
        "47425390859-0rcdnnjj7uen2kgk0ck136id25ii3pig.apps.googleusercontent.com";
      await GoogleAuth.initialize({ clientId: IOS_CLIENT_ID, scopes: ["profile", "email"] });
      const googleUser = await GoogleAuth.signIn();
      const credential = GoogleAuthProvider.credential(
        googleUser.authentication.idToken
      );
      await reauthenticateWithCredential(user, credential);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const ownerId = localStorage.getItem("ownerId");
      const email = localStorage.getItem("email");
      const database = getDatabase();

      // Step 1: Re-authenticate — Firebase requires fresh credentials for deleteUser
      await reauthenticate();

      // Step 2: Best-effort database cleanup — silently ignore permission errors
      await Promise.allSettled([
        ownerId && remove(ref(database, `customers/${ownerId}`)),
        ownerId && remove(ref(database, `menu/${ownerId}`)),
        ownerId && remove(ref(database, `tables/${ownerId}`)),
        ownerId && remove(ref(database, `waiters/${ownerId}`)),
        ownerId && remove(ref(database, `settings/${ownerId}`)),
      ].filter(Boolean));

      // Find and remove userInfo + user entries by email (best-effort)
      await Promise.allSettled([
        get(ref(database, "userInfo")).then((snap) => {
          if (!snap.exists()) return;
          for (const [key, val] of Object.entries(snap.val())) {
            if (val.email === email) return remove(ref(database, `userInfo/${key}`));
          }
        }),
        get(ref(database, "user")).then((snap) => {
          if (!snap.exists()) return;
          for (const [key, val] of Object.entries(snap.val())) {
            if (val === email) return remove(ref(database, `user/${key}`));
          }
        }),
      ]);

      // Step 3: Delete Firebase Auth account — this is what Apple requires
      if (auth.currentUser) {
        await deleteUser(auth.currentUser);
      }

      localStorage.clear();
      navigate("/");
    } catch (err) {
      setDeleting(false);
      setShowDeleteConfirm(false);
      // User dismissed the Google/Apple re-auth sheet — don't show error
      if (err?.code === "1001" || err?.message?.includes("cancel")) return;
      alert("Failed to delete account. Please try again.");
    }
  };

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  return (
    <div
      className="min-h-screen bg-surface pb-20"
      style={{ paddingTop: isIOS ? 'env(safe-area-inset-top)' : '0' }}
    >
      <div className="max-w-md mx-auto relative">
        <div
          className="absolute right-4 z-50"
          ref={menuRef}
          style={{ top: isIOS ? 'calc(env(safe-area-inset-top) + 0.75rem)' : '1rem' }}
        >
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="w-9 h-9 rounded-xl bg-white border border-primary/30 shadow-[0_2px_10px_rgba(0,0,0,0.12)] flex items-center justify-center text-primary"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="5" r="1.5" />
              <circle cx="12" cy="12" r="1.5" />
              <circle cx="12" cy="19" r="1.5" />
            </svg>
          </button>
          {menuOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-2xl shadow-xl border border-gray-100 py-1 overflow-hidden">
              <button
                onClick={() => { setMenuOpen(false); navigate("/about"); }}
                className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5"
              >
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                About
              </button>
              {!isWaiter && (
                <button
                  onClick={() => { setMenuOpen(false); navigate("/profile"); }}
                  className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5"
                >
                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Edit Profile
                </button>
              )}
              {isWaiter && (
                <div className="px-4 py-2 border-t border-gray-100">
                  <p className="text-xs text-gray-400">
                    Signed in as {localStorage.getItem("waiterName") || "Waiter"}
                  </p>
                </div>
              )}
              <div className="border-t border-gray-100">
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-3 text-sm text-red-500 hover:bg-red-50 flex items-center gap-2.5"
                >
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Log out
                </button>
              </div>
              {isIOS && !isWaiter && (
                <div className="border-t border-gray-100">
                  <button
                    onClick={() => { setMenuOpen(false); setShowDeleteConfirm(true); }}
                    className="w-full text-left px-4 py-3 text-sm text-red-400 hover:bg-red-50 flex items-center gap-2.5"
                  >
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete Account
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        {children}
      </div>
      <BottomNav isAdmin={isAdmin} isWaiter={isWaiter} />

      {/* Delete Account confirmation modal — iOS only */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mx-auto mb-4">
              <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                  d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-gray-900 text-center mb-2">
              Delete Account?
            </h2>
            <p className="text-sm text-gray-500 text-center mb-6 leading-relaxed">
              This will permanently delete your account and all data — customers, menu, tables, and staff. This cannot be undone.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="w-full py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-semibold disabled:opacity-60"
              >
                {deleting ? "Deleting…" : "Yes, Delete My Account"}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="w-full py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Layout;
